import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LiveTelemetry } from '../services/droneApi';
import type { DroneAccessScope, DroneEquipment, TriggerTemplate } from '@/types';

/** API-owned per-drone switch (SCRUM-1585): triggers only run while 'autonomous'. */
export type DroneControlMode = 'autonomous' | 'controlled';

export interface DroneStream {
  cameraId: string;
  url: string;
}

export interface DroneLog {
  timestamp: string;
  message: string;
  time: string;
  log: string;
  level?: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
  metadata?: unknown;
}

export interface ControllerInputData {
  droneId: string;
  data: {
    id: string;
    index: number;
    buttons: Array<{ pressed: boolean; value: number }>;
    axes: number[];
    mapped: {
      throttle: number;
      yaw: number;
      pitch: number;
      roll: number;
    };
  };
}

export interface LiveDrone {
  id: string;
  name: string;
  type?: string;
  accessScope?: DroneAccessScope;
  lastTelemetry?: LiveTelemetry;
  streams: DroneStream[];
  lastFrameUrls: Record<string, string>; // cameraId -> url
  logs: DroneLog[];
  isOnline: boolean;
  lastSeenAt?: string;
  equipment?: DroneEquipment[]; // Array of equipment attached to the drone
  triggerConfig?: TriggerTemplate | null;
  controlMode?: DroneControlMode;
}

export interface DroneLiveState {
  drones: Record<string, LiveDrone>; // droneId -> drone data
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
}

// Incoming drone data from WebSocket
export interface IncomingDroneData {
  id: string;
  name: string;
  type?: string;
  accessScope?: DroneAccessScope;
  lastTelemetry?: LiveTelemetry;
  streams?: DroneStream[];
  lastFrameUrls?: string[];
  lastSeenAt?: string;
  equipment?: DroneEquipment[];
  controlMode?: DroneControlMode;
}

const initialState: DroneLiveState = {
  drones: {},
  connectionStatus: 'disconnected',
};

const droneLiveSlice = createSlice({
  name: 'droneLive',
  initialState,
  reducers: {
    // Action to start WebSocket (handled by middleware)
    startWebSocket: (state) => {
      state.connectionStatus = 'connecting';
    },
    // Action to stop WebSocket (handled by middleware)
    stopWebSocket: () => {},

    setConnectionStatus: (
      state,
      action: PayloadAction<'disconnected' | 'connecting' | 'connected' | 'error'>,
    ) => {
      state.connectionStatus = action.payload;
    },

    setDrones: (state, action: PayloadAction<IncomingDroneData[]>) => {
      const drones: Record<string, LiveDrone> = {};
      action.payload.forEach((drone) => {
        drones[drone.id] = {
          id: drone.id,
          name: drone.name,
          type: drone.type,
          accessScope: drone.accessScope,
          lastTelemetry: drone.lastTelemetry,
          streams: drone.streams || [],
          lastFrameUrls: {},
          // keep diagnostics across operator socket reconnects
          logs: state.drones[drone.id]?.logs ?? [],
          isOnline: true,
          lastSeenAt: drone.lastSeenAt,
          equipment: drone.equipment || [],
          controlMode: drone.controlMode,
        };

        // Convert lastFrameUrls array to object
        if (drone.lastFrameUrls && Array.isArray(drone.lastFrameUrls)) {
          drone.lastFrameUrls.forEach((url: string) => {
            // Extract cameraId from URL pattern: /cameras/{cameraId}/
            const match = url.match(/\/cameras\/([^/]+)\//);
            if (match) {
              drones[drone.id].lastFrameUrls[match[1]] = url;
            }
          });
        }
      });
      state.drones = drones;
    },

    updateDroneTelemetry: (
      state,
      action: PayloadAction<{
        droneId: string;
        telemetry: LiveTelemetry;
        controlMode?: DroneControlMode;
      }>,
    ) => {
      const { droneId, telemetry, controlMode } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].lastTelemetry = telemetry;
        state.drones[droneId].lastSeenAt = new Date().toISOString();
        if (controlMode) {
          state.drones[droneId].controlMode = controlMode;
        }
      }
    },

    updateDroneControlMode: (
      state,
      action: PayloadAction<{ droneId: string; mode: DroneControlMode }>,
    ) => {
      const { droneId, mode } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].controlMode = mode;
      }
    },

    addDroneStream: (
      state,
      action: PayloadAction<{ droneId: string; cameraId: string; url: string }>,
    ) => {
      const { droneId, cameraId, url } = action.payload;
      if (state.drones[droneId]) {
        const existingIndex = state.drones[droneId].streams.findIndex(
          (s) => s.cameraId === cameraId,
        );
        if (existingIndex >= 0) {
          state.drones[droneId].streams[existingIndex].url = url;
        } else {
          state.drones[droneId].streams.push({ cameraId, url });
        }
      }
    },

    removeDroneStream: (state, action: PayloadAction<{ droneId: string; cameraId: string }>) => {
      const { droneId, cameraId } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].streams = state.drones[droneId].streams.filter(
          (s) => s.cameraId !== cameraId,
        );
        // drop the stale snapshot so previews don't keep showing a stopped camera
        delete state.drones[droneId].lastFrameUrls[cameraId];
      }
    },

    updateDroneLastFrame: (
      state,
      action: PayloadAction<{ droneId: string; cameraId: string; url: string }>,
    ) => {
      const { droneId, cameraId, url } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].lastFrameUrls[cameraId] = url;
      }
    },

    addDroneLog: (state, action: PayloadAction<{ droneId: string; log: DroneLog }>) => {
      const { droneId, log } = action.payload;
      if (state.drones[droneId]) {
        // Keep only last 50 logs
        state.drones[droneId].logs.unshift(log);
        if (state.drones[droneId].logs.length > 50) {
          state.drones[droneId].logs = state.drones[droneId].logs.slice(0, 50);
        }
      }
    },

    addOrUpdateDrone: (state, action: PayloadAction<IncomingDroneData>) => {
      const drone = action.payload;
      const existingDrone = state.drones[drone.id];

      if (existingDrone) {
        // Update existing drone - preserve existing data if not provided
        state.drones[drone.id] = {
          ...existingDrone,
          name: drone.name,
          type: drone.type,
          accessScope: drone.accessScope ?? existingDrone.accessScope,
          lastTelemetry: drone.lastTelemetry || existingDrone.lastTelemetry,
          streams: drone.streams || existingDrone.streams,
          equipment: drone.equipment || existingDrone.equipment,
          isOnline: true,
          lastSeenAt: drone.lastSeenAt || new Date().toISOString(),
          controlMode: drone.controlMode || existingDrone.controlMode,
        };
      } else {
        // Add new drone
        state.drones[drone.id] = {
          id: drone.id,
          name: drone.name,
          type: drone.type,
          accessScope: drone.accessScope,
          lastTelemetry: drone.lastTelemetry,
          streams: drone.streams || [],
          lastFrameUrls: {},
          logs: [],
          isOnline: true,
          lastSeenAt: drone.lastSeenAt || new Date().toISOString(),
          equipment: drone.equipment || [],
          controlMode: drone.controlMode,
        };
      }

      // Convert lastFrameUrls array to object if present
      if (drone.lastFrameUrls && Array.isArray(drone.lastFrameUrls)) {
        drone.lastFrameUrls.forEach((url: string) => {
          const match = url.match(/\/cameras\/([^/]+)\//);
          if (match) {
            state.drones[drone.id].lastFrameUrls[match[1]] = url;
          }
        });
      }
    },

    // Local sync after a successful equipment PATCH — the socket does not
    // broadcast equipment changes, so the live copy would stay stale.
    patchDroneEquipment: (
      state,
      action: PayloadAction<{
        droneId: string;
        equipmentId: string;
        changes: Partial<DroneEquipment>;
      }>,
    ) => {
      const { droneId, equipmentId, changes } = action.payload;
      const drone = state.drones[droneId];
      if (!drone?.equipment) return;
      drone.equipment = drone.equipment.map((eq) =>
        eq.id === equipmentId ? { ...eq, ...changes } : eq,
      );
    },

    setDroneOnline: (state, action: PayloadAction<string>) => {
      const droneId = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].isOnline = true;
        state.drones[droneId].lastSeenAt = new Date().toISOString();
      }
    },

    setDroneOffline: (state, action: PayloadAction<string>) => {
      const droneId = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].isOnline = false;
        state.drones[droneId].streams = [];
        state.drones[droneId].lastFrameUrls = {};
      }
    },

    clearDroneLogs: (state, action: PayloadAction<string>) => {
      const droneId = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].logs = [];
      }
    },

    updateDroneTriggerConfig: (
      state,
      action: PayloadAction<{ droneId: string; config: TriggerTemplate | null }>,
    ) => {
      const { droneId, config } = action.payload;
      if (state.drones[droneId]) {
        state.drones[droneId].triggerConfig = config;
      }
    },

    // Actions that emit via WebSocket (handled by middleware, no state change)
    sendControllerInput: (_state, _action: PayloadAction<ControllerInputData>) => {
      // Handled by middleware - emits 'controller-input' event
    },
    sendDroneCommand: (
      _state,
      _action: PayloadAction<{ droneId: string; command: string; data?: Record<string, unknown> }>,
    ) => {
      // Handled by middleware - emits 'drone-command' event
    },
    setDroneControlMode: (
      _state,
      _action: PayloadAction<{ droneId: string; mode: DroneControlMode }>,
    ) => {
      // Handled by middleware - emits 'request:control-mode:set'; the confirmed
      // mode comes back via the 'drone-control-mode' broadcast
    },
  },
});

export const {
  startWebSocket,
  stopWebSocket,
  setConnectionStatus,
  setDrones,
  updateDroneTelemetry,
  addDroneStream,
  removeDroneStream,
  updateDroneLastFrame,
  addDroneLog,
  addOrUpdateDrone,
  patchDroneEquipment,
  setDroneOnline,
  setDroneOffline,
  clearDroneLogs,
  updateDroneTriggerConfig,
  updateDroneControlMode,
  sendControllerInput,
  sendDroneCommand,
  setDroneControlMode,
} = droneLiveSlice.actions;

export default droneLiveSlice.reducer;
